import * as React from 'react';

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { DND_CSS, dndArrayMove, useDndSortable } from './utilities';

type DndSortableContextProps = {
  dnd: React.ComponentPropsWithoutRef<typeof DndContext>;
  sortable: React.ComponentPropsWithoutRef<typeof SortableContext>;
};

const DndSortableContext = ({
  dnd: {
    sensors,
    collisionDetection = closestCenter,
    modifiers = [
      restrictToVerticalAxis,
      restrictToWindowEdges,
      restrictToFirstScrollableAncestor,
    ],
    ...otherDndProps
  },
  sortable: {
    items = [],
    strategy = verticalListSortingStrategy,
    children,
    ...otherSortableProps
  },
}: DndSortableContextProps) => {
  const defaultSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const sensorsConfig: typeof sensors = sensors ?? defaultSensors;

  return (
    <DndContext
      sensors={sensorsConfig}
      collisionDetection={collisionDetection}
      modifiers={modifiers}
      {...otherDndProps}
    >
      <SortableContext
        items={items}
        strategy={strategy}
        {...otherSortableProps}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

export { DND_CSS, dndArrayMove, DndSortableContext, useDndSortable };
